/* ItemEditorModal: Added support for External Logo URL in App Integrations */
import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';
import { Input, Select } from './Input';
import { Button } from './Button';
import { ImageUpload } from './ImageUpload';
import { useStore } from '../../store/StoreContext';
import { STATUS_MAP, normalizeStatus } from '../../utils/helpers';
import SafeIcon from '../../common/SafeIcon';

export const ItemEditorModal = ({ isOpen, onClose, item, projectId, type }) => {
  const { state, updateEntity } = useStore();
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [newUrl, setNewUrl] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    status: 'todo',
    hours: 0,
    minutes: 0,
    description: '',
    notes: '',
    assets: [],
    isAppTask: false,
    appUrl: '',
    logoUrl: ''
  });

  useEffect(() => {
    if (item && isOpen) {
      const duration = item.durationMinutes || 0;
      setFormData({
        name: item.name || '',
        status: normalizeStatus(item.status),
        hours: Math.floor(duration / 60),
        minutes: duration % 60,
        description: item.description || '',
        notes: item.notes || '',
        assets: item.assets || [],
        isAppTask: item.isAppTask || false,
        appUrl: item.appUrl || '',
        logoUrl: item.logoUrl || ''
      });
      setActiveTab('general');
    }
  }, [item, isOpen]);

  const addAsset = (asset) => {
    setFormData(prev => ({ ...prev, assets: [...prev.assets, asset] }));
    setNewUrl('');
  };

  const removeAsset = (index) => {
    setFormData(prev => ({ ...prev, assets: prev.assets.filter((_, i) => i !== index) }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving || !projectId || item._wfIndex === undefined) return;

    const project = state.projects[projectId];
    if (!project) return;

    setSaving(true);
    const durationMinutes = (parseInt(formData.hours || 0) * 60) + parseInt(formData.minutes || 0);

    try {
      const workflows = JSON.parse(JSON.stringify(project.workflows || []));
      const wfIdx = item._wfIndex;
      const tIdx = item._taskIndex;

      if (type === 'workflow') {
        if (workflows[wfIdx]) workflows[wfIdx].name = formData.name;
      } else if (type === 'task') {
        if (workflows[wfIdx]?.tasks && workflows[wfIdx].tasks[tIdx]) {
          workflows[wfIdx].tasks[tIdx] = { ...workflows[wfIdx].tasks[tIdx], ...formData, durationMinutes };
        }
      }

      await updateEntity('projects', { ...project, workflows });
      onClose();
    } catch (err) {
      console.error("Save error:", err);
      alert("Failed to save changes.");
    } finally {
      setSaving(false);
    }
  };

  const isImage = (url) => url?.match(/\.(jpeg|jpg|gif|png|webp|svg)$/i) != null || url?.includes('supabase.co/storage/v1/object/public');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Edit ${type === 'task' ? 'Task' : 'Phase'}`}>
      <div className="flex gap-1 mb-6 bg-base-900 p-1 rounded-lg border border-base-700">
        {[
          { id: 'general', label: 'General', icon: 'Settings' },
          { id: 'details', label: 'Details & Notes', icon: 'FileText' },
          { id: 'assets', label: 'Assets', icon: 'Paperclip' }
        ].map(tab => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-md text-xs font-bold transition-all ${activeTab === tab.id ? 'bg-primary text-white shadow-lg' : 'text-gray-500 hover:text-gray-300'}`}
          >
            <SafeIcon name={tab.icon} className="w-3.5 h-3.5" />
            {tab.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        {activeTab === 'general' && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <Input label="Name" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required disabled={saving} />
            {type === 'task' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <Select label="Status" value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} options={Object.entries(STATUS_MAP).map(([val, info]) => ({ label: info.label, value: val }))} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input type="number" label="Hours" value={formData.hours} onChange={e => setFormData({ ...formData, hours: e.target.value })} min="0" />
                    <Input type="number" label="Mins" value={formData.minutes} onChange={e => setFormData({ ...formData, minutes: e.target.value })} min="0" max="59" />
                  </div>
                </div>

                <div className="pt-4 border-t border-base-700">
                  <label className="flex items-center gap-3 cursor-pointer group mb-4">
                    <div className="relative w-10 h-5">
                      <input type="checkbox" className="sr-only peer" checked={formData.isAppTask} onChange={e => setFormData({ ...formData, isAppTask: e.target.checked })} />
                      <div className="w-full h-full bg-base-700 rounded-full peer-checked:bg-primary transition-colors"></div>
                      <div className="absolute left-1 top-1 w-3 h-3 bg-white rounded-full transition-all peer-checked:left-6"></div>
                    </div>
                    <span className="text-xs font-semibold text-gray-300 group-hover:text-white">App Integration</span>
                  </label>
                  {formData.isAppTask && (
                    <div className="space-y-4 p-4 bg-base-900 rounded-xl border border-base-700">
                      <Input label="App URL" value={formData.appUrl} onChange={e => setFormData({ ...formData, appUrl: e.target.value })} placeholder="https://..." />
                      <div className="space-y-4 pt-2 border-t border-base-700/50">
                        <ImageUpload label="Upload App Logo" currentUrl={formData.logoUrl} onUpload={(url) => setFormData({ ...formData, logoUrl: url })} />
                        <Input 
                          label="OR App Logo Link" 
                          value={formData.logoUrl} 
                          onChange={e => setFormData({ ...formData, logoUrl: e.target.value })} 
                          placeholder="https://..." 
                        />
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'details' && (
          <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-400">Description</label>
              <textarea
                className="bg-base-900 border border-base-700 rounded-md px-3 py-2 text-sm text-white focus:border-primary min-h-[120px] resize-none focus:outline-none"
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                placeholder="What needs to be done?"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-medium text-gray-400">Internal Notes</label>
              <textarea
                className="bg-base-900 border border-base-700 rounded-md px-3 py-2 text-sm text-gray-300 italic min-h-[80px] resize-none focus:outline-none"
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Private team notes..."
              />
            </div>
          </div>
        )}

        {activeTab === 'assets' && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ImageUpload label="Upload Image Asset" onUpload={(url) => addAsset({ type: 'file', url, name: 'Uploaded Image' })} />
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-gray-400">Add Image/File URL</label>
                <div className="flex gap-2 items-start">
                  {isImage(newUrl) && (
                    <div className="w-10 h-10 rounded bg-base-800 border-2 border-primary overflow-hidden shrink-0 shadow-lg">
                      <img src={newUrl} alt="Preview" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <input
                    className="flex-1 bg-base-900 border border-base-700 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-primary"
                    placeholder="https://..."
                    value={newUrl}
                    onChange={e => setNewUrl(e.target.value)}
                  />
                  <Button variant="secondary" type="button" onClick={() => addAsset({ type: 'url', url: newUrl, name: 'External Link' })} disabled={!newUrl}>Add</Button>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-[10px] font-bold text-gray-500 uppercase tracking-widest">Current Assets ({formData.assets.length})</label>
              <div className="space-y-2">
                {formData.assets.map((asset, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-base-900 rounded-lg border border-base-700 group">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-10 h-10 rounded bg-base-800 border border-base-700 flex items-center justify-center shrink-0 overflow-hidden">
                        {isImage(asset.url) ? <img src={asset.url} alt="" className="w-full h-full object-cover" /> : <SafeIcon name="Link" className="w-4 h-4 text-gray-600" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-white truncate">{asset.name}</div>
                        <a href={asset.url} target="_blank" rel="noreferrer" className="text-[10px] text-gray-500 hover:text-primary truncate block underline">{asset.url}</a>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeAsset(index)} className="p-2 text-gray-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                {formData.assets.length === 0 && (
                  <p className="text-xs text-gray-500 italic text-center py-4 bg-base-900/30 rounded-lg border border-dashed border-base-700">No assets attached to this {type}.</p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-6 gap-3 border-t border-base-700 mt-auto">
          <Button variant="ghost" type="button" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
        </div>
      </form>
    </Modal>
  );
};